import { useEffect, useState } from 'react';
import Icon from './Icon';
import { api } from '../api/client';

/**
 * Shows the uploaded ID proof inline. The bytes come through an authorized fetch
 * rather than a plain URL, because the document endpoint requires a bearer token —
 * so the blob has to be created here and released when the screen goes away.
 */
export default function DocumentViewer({ documentId, filename, contentType }) {
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentId) return undefined;
    let objectUrl = null;
    let cancelled = false;

    api
      .documentUrl(documentId)
      .then((created) => {
        objectUrl = created;
        if (cancelled) URL.revokeObjectURL(created);
        else setUrl(created);
      })
      .catch((err) => !cancelled && setError(err));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  if (!documentId) {
    return (
      <div className="empty">
        <p style={{ margin: 0 }}>No ID proof has been uploaded yet.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty">
        <p style={{ margin: 0 }}>{error.message}</p>
      </div>
    );
  }

  const isPdf = contentType === 'application/pdf';

  return (
    <div className="doc-viewer">
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <span className="muted small">{filename}</span>
        {!isPdf && (
          <span style={{ display: 'flex', gap: 'var(--s-1)' }}>
            <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} aria-label="Zoom out">
              −
            </button>
            <span className="muted small mono" style={{ minWidth: 44, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setZoom((z) => Math.min(4, z + 0.25))} aria-label="Zoom in">
              +
            </button>
          </span>
        )}
      </div>

      <div className="doc-frame">
        {!url && <div className="empty">Loading document…</div>}
        {url && isPdf && <iframe src={url} title={filename} className="doc-pdf" />}
        {url && !isPdf && (
          <img src={url} alt={`ID proof: ${filename}`} style={{ width: `${zoom * 100}%` }} />
        )}
      </div>

      {url && (
        <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">
          <Icon name="upload" size={14} />
          Open in a new tab
        </a>
      )}
    </div>
  );
}
